import { captureMessage, init } from '@sentry/gatsby';

import { isAppleDevice } from 'utils/browser';
import { isMonitoringEnabled } from 'utils/featureFlags';
import { isNumber } from 'utils/types';

const FILTERED_ERRORS = [
  {
    name: 'NotAllowedError',
    message: 'Permission to join interest group denied',
  },
  {
    name: 'AbortError',
    message: 'The play() request was interrupted because the media was removed from the document',
  },
  {
    name: 'AbortError',
    message: 'The operation was aborted',
  },
  {
    name: 'AbortError',
    message: 'The play() request was interrupted because video-only background media was paused to save power',
  },
  {
    name: 'NotAllowedError',
    message:
      'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission',
  },
  {
    name: 'AbortError',
    message: 'The play() request was interrupted by a call to pause()',
  },
  {
    name: 'NotAllowedError',
    message: 'play() can only be initiated by a user gesture',
  },
  {
    name: 'NotAllowedError',
    message:
      'The play method is not allowed by the user agent or the platform in the current context, possibly because the user denied permission',
  },
  {
    name: 'TypeError',
    message: 'Load failed',
    condition: isAppleDevice,
  },
  {
    name: 'TypeError',
    message: 'this.o.at is not a function',
  },
  {
    name: 'SecurityError',
    message: "Failed to read a named property 'setTimeout' from 'Window'",
  },
  // Browser extension (e.g. Safari on iOS) trying to communicate with a closed tab — not our code.
  {
    name: 'Error',
    message: 'Invalid call to runtime.sendMessage()',
  },
  // Sentry SDK internal error triggered when it fails to parse the runtime.sendMessage error above — noise loop.
  {
    name: 'Error',
    message: 'Cannot parse given Error object',
  },
];

const shouldFilterError = (error) => {
  return FILTERED_ERRORS.some((filter) => {
    if (filter.condition) {
      if (!filter.condition()) {
        return false;
      }
    }

    if (filter.name) {
      if (error.name !== filter.name) {
        return false;
      }
    }

    if (filter.message) {
      if (!error.message.includes(filter.message)) {
        return false;
      }
    }

    return true;
  });
};

export class SentryService {
  static #isInitialized = false;

  static init() {
    if (typeof window === 'undefined' || this.#isInitialized) {
      return;
    }

    const sampleRate = +process.env.GATSBY_SENTRY_SAMPLE_RATE;
    const tracesSampleRate = isNumber(sampleRate) ? sampleRate : 0.01;

    init({
      tracesSampleRate,
      dsn: process.env.GATSBY_SENTRY_DSN,
      attachStacktrace: true,
      enabled: isMonitoringEnabled(),
      denyUrls: [
        /stats\.g\.doubleclick\.net/,
        /googletagmanager\.com\/gtm\.js/,
        /cdn\.cookielaw\.org/,
        /tdn\.r42tag\.com/,
        /bat\.bing\.com\/bat\.js/,
      ],
      beforeSend(event, hint) {
        const filteredByEvent = event?.exception?.values?.some((ex) =>
          shouldFilterError({ name: ex.type, message: ex.value ?? '' })
        );
        if (filteredByEvent) {
          return null;
        }

        const originalException = hint?.originalException;
        if (
          (originalException instanceof Error ||
            (typeof DOMException !== 'undefined' && originalException instanceof DOMException)) &&
          shouldFilterError(originalException)
        ) {
          return null;
        }

        const context = hint?.captureContext?.extra?.context?.[0];
        if (context && shouldFilterError(context)) {
          return null;
        }

        return event;
      },
    });

    this.#isInitialized = true;
  }

  static captureError({ message, context }) {
    captureMessage(message, {
      level: 'error',
      extra: { context },
    });
  }
}
